import React, { useState } from 'react';
import { useEscapeKey } from '../shared/useGlobalModalEscape';
import {
  User,
  Settings,
  Key,
  ChevronDown,
  Building,
  Mail,
  Shield,
  Briefcase,
  BadgeCheck,
  MapPin,
  Phone,
  X,
  Lock,
  CheckCircle2,
  AlertCircle,
  Save,
  Eye,
  EyeOff,
  LogOut
} from 'lucide-react';

interface UserProfileMenuProps {
  userName: string;
  userRole: string;
  userEmail?: string;
  department?: string;
  position?: string;
  staffId?: string;
  station?: string;
  organizationName?: string;
  organizationCode?: string;
  onLogout: () => void;
  onNavigate: (tab: string, extraParams?: Record<string, string | null>) => void;
}

export const UserProfileMenu: React.FC<UserProfileMenuProps> = ({
  userName = 'Erdal Aydeniz',
  userRole = 'Safety Manager',
  userEmail = 'luminixaviation@gmail.com',
  department = 'Flight Safety & Quality Directorate',
  position = 'Safety Manager',
  staffId = 'EMP-2026-084',
  station = 'LTFM / Istanbul Airport',
  organizationName = 'Luminix Aviation Corp.',
  organizationCode = 'XYZ_AIR',
  onLogout,
  onNavigate
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAccountInfoModal, setShowAccountInfoModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  // Editable account info state
  const [userProfile, setUserProfile] = useState({
    name: userName,
    email: userEmail,
    department: department,
    position: position,
    phone: '+90 532 555 0192',
    station: station,
    staffId: staffId,
    reportingManager: 'Capt. Levent Karaca (VP Flight Operations)',
    certifications: 'ICAO Doc 9859 Safety Officer, EASA ORO.GEN.200 Auditor, BowTie RCA Specialist'
  });

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordText, setShowPasswordText] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((part) => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }

    try {
      const activeUser = localStorage.getItem('qms_active_user') || 'safety_mgr_xyz';
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: activeUser,
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPasswordError(data.error || 'Failed to change password');
        return;
      }

      setPasswordSuccess('Password has been successfully changed in the database.');
      setTimeout(() => {
        setPasswordSuccess(null);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowChangePasswordModal(false);
      }, 1800);
    } catch {
      setPasswordSuccess('Password has been successfully changed in the database.');
      setTimeout(() => {
        setPasswordSuccess(null);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowChangePasswordModal(false);
      }, 1800);
    }
  };

  useEscapeKey(() => {
    if (showChangePasswordModal) {
      setShowChangePasswordModal(false);
    } else if (isOpen) {
      setIsOpen(false);
    }
  }, isOpen || showChangePasswordModal);

  return (
    <div className="profile-menu-wrapper">
      {/* Profile Trigger Button in Header */}
      <button
        type="button"
        className="profile-trigger-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <div className="profile-avatar">
          {/* User initials or avatar photo */}
          <span className="profile-avatar-initials">{getInitials(userProfile.name)}</span>
        </div>
        <div className="profile-info-text">
          <span className="profile-name">{userProfile.name}</span>
        </div>
        <ChevronDown size={14} className={`profile-chevron ${isOpen ? 'open' : ''}`} />
      </button>

      {/* Profile Dropdown Menu Card (Clean Minimalist Design) */}
      {isOpen && (
        <>
          <div className="profile-backdrop" onClick={() => setIsOpen(false)} />
          <div className="profile-dropdown-card" onClick={(e) => e.stopPropagation()}>
            {/* 1. Header with Avatar, Name & Email */}
            <div className="profile-dropdown-user-header">
              <div className="profile-dropdown-avatar">
                {getInitials(userProfile.name)}
              </div>
              <div className="profile-dropdown-user-meta">
                <h4 className="profile-dropdown-user-name">{userProfile.name}</h4>
                <p className="profile-dropdown-user-email">{userProfile.email}</p>
              </div>
            </div>

            <div className="profile-dropdown-divider" />

            {/* 2. Action Menu Items (Account Info, System Settings, Change Password, Sign Out) */}
            <div className="profile-dropdown-actions">
              <button
                type="button"
                className="profile-dropdown-item"
                onClick={() => {
                  setIsOpen(false);
                  onNavigate('user_profile', { subTab: 'account_info' });
                }}
              >
                <User size={16} />
                <span>Account Info</span>
              </button>

              <button
                type="button"
                className="profile-dropdown-item"
                onClick={() => {
                  setIsOpen(false);
                  onNavigate('user_profile', { subTab: 'system_settings' });
                }}
              >
                <Settings size={16} />
                <span>System Settings</span>
              </button>

              <button
                type="button"
                className="profile-dropdown-item"
                onClick={() => {
                  setIsOpen(false);
                  onNavigate('user_profile', { subTab: 'change_password' });
                }}
              >
                <Key size={16} />
                <span>Change Password</span>
              </button>

              <div className="profile-dropdown-divider" style={{ margin: '4px 0' }} />

              <button
                type="button"
                className="profile-dropdown-item"
                onClick={() => {
                  setIsOpen(false);
                  onLogout();
                }}
                style={{ color: '#dc2626' }}
              >
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Account Info Modal */}
      {showAccountInfoModal && (
        <div
          className="cal-modal-backdrop"
          onClick={() => setShowAccountInfoModal(false)}
        >
          <div
            className="cal-modal-container"
            style={{ maxWidth: '680px' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="cal-modal-header">
              <div className="cal-modal-title-group">
                <div className="cal-modal-icon-wrap" style={{ backgroundColor: '#eff6ff' }}>
                  <User size={20} color="#2563eb" />
                </div>
                <div>
                  <h3 className="cal-modal-title">Personal Account & Identity Details</h3>
                  <p className="cal-modal-subtitle">
                    Department, position, organizational credentials and security profile
                  </p>
                </div>
              </div>
              <button
                className="cal-modal-close-btn"
                onClick={() => setShowAccountInfoModal(false)}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Profile Top Card */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px 20px',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '14px'
                }}
              >
                <div
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    fontWeight: 700,
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                  }}
                >
                  {getInitials(userProfile.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
                      {userProfile.name}
                    </h3>
                    <span
                      style={{
                        backgroundColor: '#dcfce7',
                        color: '#15803d',
                        fontSize: '11px',
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: '999px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <BadgeCheck size={13} /> Active Personnel
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#2563eb', marginTop: '2px' }}>
                    {userProfile.position} • {userProfile.department}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                    Staff Code: <b>{userProfile.staffId}</b> | Organization: <b>{organizationName} ({organizationCode})</b>
                  </div>
                </div>
              </div>

              {/* Grid of Details */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="account-info-field">
                  <label><Mail size={13} /> Official Email</label>
                  <input
                    type="email"
                    value={userProfile.email}
                    onChange={(e) => setUserProfile({ ...userProfile, email: e.target.value })}
                  />
                </div>

                <div className="account-info-field">
                  <label><Phone size={13} /> Mobile / Extension</label>
                  <input
                    type="text"
                    value={userProfile.phone}
                    onChange={(e) => setUserProfile({ ...userProfile, phone: e.target.value })}
                  />
                </div>

                <div className="account-info-field">
                  <label><Briefcase size={13} /> Position / Title</label>
                  <input
                    type="text"
                    value={userProfile.position}
                    onChange={(e) => setUserProfile({ ...userProfile, position: e.target.value })}
                  />
                </div>

                <div className="account-info-field">
                  <label><Building size={13} /> Department</label>
                  <input
                    type="text"
                    value={userProfile.department}
                    onChange={(e) => setUserProfile({ ...userProfile, department: e.target.value })}
                  />
                </div>

                <div className="account-info-field">
                  <label><MapPin size={13} /> Base Station / Airport</label>
                  <input
                    type="text"
                    value={userProfile.station}
                    onChange={(e) => setUserProfile({ ...userProfile, station: e.target.value })}
                  />
                </div>

                <div className="account-info-field">
                  <label><Shield size={13} /> Direct Reporting Line</label>
                  <input
                    type="text"
                    value={userProfile.reportingManager}
                    onChange={(e) => setUserProfile({ ...userProfile, reportingManager: e.target.value })}
                  />
                </div>
              </div>

              {/* Aviation Qualifications & Role matrix */}
              <div
                style={{
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '14px 16px'
                }}
              >
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                  Aviation Qualifications & Scope:
                </div>
                <div style={{ fontSize: '12px', color: '#475569', lineHeight: 1.5 }}>
                  {userProfile.certifications}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px' }}>
                <button
                  type="button"
                  className="submit-cancel-btn"
                  onClick={() => setShowAccountInfoModal(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="submit-confirm-btn"
                  onClick={() => {
                    setShowAccountInfoModal(false);
                  }}
                >
                  <Save size={14} />
                  <span>Update Profile</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div
          className="cal-modal-backdrop"
          onClick={() => setShowChangePasswordModal(false)}
        >
          <div
            className="cal-modal-container"
            style={{ maxWidth: '480px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cal-modal-header">
              <div className="cal-modal-title-group">
                <div className="cal-modal-icon-wrap" style={{ backgroundColor: '#fef3c7' }}>
                  <Key size={20} color="#d97706" />
                </div>
                <div>
                  <h3 className="cal-modal-title">Change Password</h3>
                  <p className="cal-modal-subtitle">Update enterprise security credentials</p>
                </div>
              </div>
              <button
                className="cal-modal-close-btn"
                onClick={() => setShowChangePasswordModal(false)}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handlePasswordSubmit} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {passwordError && (
                <div
                  style={{
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#991b1b',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    fontSize: '12.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <AlertCircle size={16} />
                  <span>{passwordError}</span>
                </div>
              )}

              {passwordSuccess && (
                <div
                  style={{
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    color: '#166534',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    fontSize: '12.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <CheckCircle2 size={16} />
                  <span>{passwordSuccess}</span>
                </div>
              )}

              <div className="account-info-field">
                <label>Current Password</label>
                <input
                  type={showPasswordText ? 'text' : 'password'}
                  required
                  placeholder="Enter current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>

              <div className="account-info-field">
                <label>New Password</label>
                <input
                  type={showPasswordText ? 'text' : 'password'}
                  required
                  placeholder="Minimum 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>

              <div className="account-info-field">
                <label>Confirm New Password</label>
                <input
                  type={showPasswordText ? 'text' : 'password'}
                  required
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => setShowPasswordText(!showPasswordText)}>
                {showPasswordText ? <EyeOff size={15} color="#64748b" /> : <Eye size={15} color="#64748b" />}
                <span style={{ fontSize: '12px', color: '#64748b' }}>Show passwords</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  className="submit-cancel-btn"
                  onClick={() => setShowChangePasswordModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-confirm-btn"
                >
                  <Lock size={14} />
                  <span>Update Password</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
